/* eslint-disable react-hooks/exhaustive-deps */
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BadgeIcon from "@mui/icons-material/Badge";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EmailIcon from "@mui/icons-material/Email";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TablePreloader from "../../../components/Loader/TablePreloader";
import { getUserById, getUserData } from "../../../features/users/userSlice";
import HomeLayout from "../../../layouts/Home";

const UserProfile = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { user, loading } = useSelector(getUserData);

  useEffect(() => {
    if (id && !state) {
      dispatch(getUserById(id));
    }
  }, [id]);

  const userData = state || user;

  // Helper function to get role display info
  const getRoleInfo = (role) => {
    const roleMap = {
      companyadmin: {
        label: "Company Admin",
        icon: <BusinessIcon />,
        color: "#8b5cf6",
      },
      individual: {
        label: "Individual",
        icon: <PersonIcon />,
        color: "#8b5cf6",
      },
      hmoadmin: {
        label: "HMO Admin",
        icon: <LocalHospitalIcon />,
        color: "#8b5cf6",
      },
      hospitaladmin: {
        label: "Hospital Admin",
        icon: <LocalHospitalIcon />,
        color: "#8b5cf6",
      },
      superadmin: {
        label: "Super Admin",
        icon: <AdminPanelSettingsIcon />,
        color: "#8b5cf6",
      },
    };
    return (
      roleMap[role?.toLowerCase()] || {
        label: role,
        icon: <PersonIcon />,
        color: "#8b5cf6",
      }
    );
  };

  const roleInfo = getRoleInfo(userData?.role?.[0]);

  // Helper function to render info row
  const InfoRow = ({ icon, label, value, chip }) => (
    <Box className="flex items-center justify-between py-4 px-2 hover:bg-gray-50 transition-colors rounded-lg">
      <Box className="flex items-center gap-3">
        <Box
          className="p-2 rounded-lg"
          sx={{ backgroundColor: `${roleInfo.color}15`, color: roleInfo.color }}
        >
          {icon}
        </Box>
        <Typography className="text-[#6B7588] font-medium">{label}</Typography>
      </Box>
      {chip ? (
        chip
      ) : (
        <Typography className="text-[#040316] font-semibold">
          {value || "N/A"}
        </Typography>
      )}
    </Box>
  );

  if (loading && !userData) {
    return (
      <HomeLayout title="User Profile">
        <TablePreloader />
      </HomeLayout>
    );
  }

  return (
    <HomeLayout title="User Profile">
      {/* Header */}
      <Box className="flex items-center gap-2 mb-6">
        <ArrowBackIcon
          onClick={() => navigate(-1)}
          className="cursor-pointer text-[var(--c-dark-1)] hover:text-[var(--c-primary-0)] transition-colors"
          fontSize="medium"
        />
        <Typography className="text-[16px] font-medium text-[var(--c-dark-1)]">
          Go back
        </Typography>
      </Box>

      {/* Profile Header Card */}
      <Card className="mb-6 shadow-sm">
        <CardContent className="p-8">
          <Box className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <Avatar
              alt={`${userData?.firstName} ${userData?.lastName}`}
              src={userData?.profileImage}
              className="w-32 h-32 border-4 shadow-lg"
              sx={{ borderColor: roleInfo.color }}
            >
              <Typography variant="h2" className="font-bold">
                {userData?.firstName?.[0]}
                {userData?.lastName?.[0]}
              </Typography>
            </Avatar>

            {/* User Info */}
            <Box className="flex-1 text-center md:text-left">
              <Typography
                variant="h4"
                className="font-bold text-[#040316] mb-2"
              >
                {userData?.firstName} {userData?.lastName}
              </Typography>
              <Box className="flex flex-wrap gap-2 justify-center md:justify-start mb-3">
                <Chip
                  icon={roleInfo.icon}
                  label={roleInfo.label}
                  sx={{
                    backgroundColor: roleInfo.color,
                    color: "white",
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    height: "32px",
                  }}
                />
                {userData?.isActive !== undefined && (
                  <Chip
                    icon={<VerifiedUserIcon />}
                    label={userData?.isActive ? "Active" : "Inactive"}
                    color={userData?.isActive ? "success" : "default"}
                    variant="outlined"
                    sx={{ height: "32px", fontWeight: 500 }}
                  />
                )}
              </Box>
              <Typography className="text-[#6B7588] text-base">
                {userData?.email}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card className="shadow-sm h-full">
            <CardContent className="p-6">
              <Typography
                variant="h6"
                className="font-bold text-[#040316] mb-4"
              >
                Contact Information
              </Typography>
              <Divider className="mb-4" />
              <InfoRow
                icon={<EmailIcon />}
                label="Email Address"
                value={userData?.email}
              />
              <InfoRow
                icon={<PhoneIcon />}
                label="Phone Number"
                value={userData?.phoneNumber}
              />
              <InfoRow
                icon={<LocationOnIcon />}
                label="Address"
                value={
                  userData?.street || userData?.city || userData?.state
                    ? `${userData?.street || ""}${
                        userData?.street && userData?.city ? ", " : ""
                      }${userData?.city || ""}${
                        userData?.city && userData?.state ? ", " : ""
                      }${userData?.state || ""}`
                    : undefined
                }
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Account Information */}
        <Grid item xs={12} md={6}>
          <Card className="shadow-sm h-full">
            <CardContent className="p-6">
              <Typography
                variant="h6"
                className="font-bold text-[#040316] mb-4"
              >
                Account Information
              </Typography>
              <Divider className="mb-4" />
              <InfoRow
                icon={<BadgeIcon />}
                label="User ID"
                value={userData?.id}
              />
              <InfoRow
                icon={<AdminPanelSettingsIcon />}
                label="Role"
                chip={
                  <Chip
                    icon={roleInfo.icon}
                    label={roleInfo.label}
                    size="small"
                    sx={{
                      backgroundColor: `${roleInfo.color}20`,
                      color: roleInfo.color,
                      fontWeight: 600,
                    }}
                  />
                }
              />
              <InfoRow
                icon={<CalendarTodayIcon />}
                label="Created At"
                value={
                  userData?.createdAt
                    ? dayjs(userData.createdAt).format("MMM DD, YYYY - HH:mm")
                    : undefined
                }
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Organization Affiliations */}
        {(userData?.companies?.length > 0 ||
          userData?.hospitals?.length > 0 ||
          userData?.hmos?.length > 0) && (
          <Grid item xs={12}>
            <Card className="shadow-sm">
              <CardContent className="p-6">
                <Typography
                  variant="h6"
                  className="font-bold text-[#040316] mb-4"
                >
                  Organization Affiliations
                </Typography>
                <Divider className="mb-4" />
                <Grid container spacing={3}>
                  {/* Companies */}
                  {userData?.companies?.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box className="p-4 bg-purple-50 rounded-lg">
                        <Box className="flex items-center gap-2 mb-3">
                          <BusinessIcon sx={{ color: "#8b5cf6" }} />
                          <Typography className="font-semibold text-[#8b5cf6]">
                            Company
                          </Typography>
                        </Box>
                        {userData.companies.map((company, index) => (
                          <Typography
                            key={index}
                            className="text-[#040316] mb-1 pl-2"
                          >
                            • {company?.name || "N/A"}
                          </Typography>
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {/* Hospitals */}
                  {userData?.hospitals?.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box className="p-4 bg-purple-50 rounded-lg">
                        <Box className="flex items-center gap-2 mb-3">
                          <LocalHospitalIcon sx={{ color: "#06b6d4" }} />
                          <Typography className="font-semibold text-[#06b6d4]">
                            Hospital
                          </Typography>
                        </Box>
                        {userData.hospitals.map((hospital, index) => (
                          <Typography
                            key={index}
                            className="text-[#040316] mb-1 pl-2"
                          >
                            • {hospital?.name || "N/A"}
                          </Typography>
                        ))}
                      </Box>
                    </Grid>
                  )}

                  {/* HMOs */}
                  {userData?.hmos?.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Box className="p-4 bg-purple-50 rounded-lg">
                        <Box className="flex items-center gap-2 mb-3">
                          <LocalHospitalIcon sx={{ color: "#8b5cf6" }} />
                          <Typography className="font-semibold text-[#8b5cf6]">
                            HMO
                          </Typography>
                        </Box>
                        {userData.hmos.map((hmo, index) => (
                          <Typography
                            key={index}
                            className="text-[#040316] mb-1 pl-2"
                          >
                            • {hmo?.name || "N/A"}
                          </Typography>
                        ))}
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </HomeLayout>
  );
};

export default UserProfile;
